import useOnMount from 'hooks/useOnMount'
import { serializeForInlineScript, useScriptInit } from 'utils/scriptUtils'
import type { RootBootstrapData } from './root/types'
import type { KeysValue } from 'services/KeysProvider'

const GTM_ID = 'GTM-KPZ2SGN'

export function KeysScript({ keys }: { keys: Record<string, any> }) {
  return (
    <script
      type="text/javascript"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: `KEYS = ${serializeForInlineScript(keys)}`,
      }}
    />
  )
}

// Serializes the root loader subset for synchronous pre-hydration reads (see
// getRootLoaderData).
export function RootBootstrapScript({ data }: { data: RootBootstrapData }) {
  return (
    <script
      type="text/javascript"
      // eslint-disable-next-line react/no-danger
      dangerouslySetInnerHTML={{
        __html: `window.__ROOT_BOOTSTRAP__ = ${serializeForInlineScript(data)}`,
      }}
    />
  )
}

export function BodyScripts({ keys }: { keys: KeysValue }) {
  const onAcsbLoad = useScriptInit({
    hasLoadedFn: () => !!window.acsbJS,
    initFn: () => {
      window.acsbJS.init({
        statementLink: '',
        footerHtml: '',
        hideMobile: false,
        hideTrigger: true,
        language: 'en',
        position: 'left',
        leadColor: '#FF0072',
        triggerColor: '#FF0072',
        triggerRadius: '50%',
        triggerPositionX: 'right',
        triggerPositionY: 'bottom',
        triggerIcon: 'people',
        triggerSize: 'medium',
        triggerOffsetX: 20,
        triggerOffsetY: 20,
        mobile: {
          triggerSize: 'small',
          triggerPositionX: 'right',
          triggerPositionY: 'bottom',
          triggerOffsetX: 10,
          triggerOffsetY: 10,
          triggerRadius: '50%',
        },
      })
    },
  })

  useOnMount(() => {
    window._tfa = window._tfa || []
    window._tfa.push({ notify: 'event', name: 'page_view', id: 1263317 })
  })

  return (
    <>
      <script
        type="text/javascript"
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `+function(a,p,P,b,y){a.braze={};a.brazeQueue=[];for(var s="BrazeSdkMetadata DeviceProperties Card Card.prototype.dismissCard Card.prototype.removeAllSubscriptions Card.prototype.removeSubscription Card.prototype.subscribeToClickedEvent Card.prototype.subscribeToDismissedEvent Card.fromContentCardsJson ImageOnly CaptionedImage ClassicCard ControlCard ContentCards ContentCards.prototype.getUnviewedCardCount Feed Feed.prototype.getUnreadCardCount ControlMessage InAppMessage InAppMessage.SlideFrom InAppMessage.ClickAction InAppMessage.DismissType InAppMessage.OpenTarget InAppMessage.ImageStyle InAppMessage.Orientation InAppMessage.TextAlignment InAppMessage.CropType InAppMessage.prototype.closeMessage InAppMessage.prototype.removeAllSubscriptions InAppMessage.prototype.removeSubscription InAppMessage.prototype.subscribeToClickedEvent InAppMessage.prototype.subscribeToDismissedEvent InAppMessage.fromJson FullScreenMessage ModalMessage HtmlMessage SlideUpMessage User User.Genders User.NotificationSubscriptionTypes User.prototype.addAlias User.prototype.addToCustomAttributeArray User.prototype.addToSubscriptionGroup User.prototype.getUserId User.prototype.getUserId User.prototype.incrementCustomUserAttribute User.prototype.removeFromCustomAttributeArray User.prototype.removeFromSubscriptionGroup User.prototype.setCountry User.prototype.setCustomLocationAttribute User.prototype.setCustomUserAttribute User.prototype.setDateOfBirth User.prototype.setEmail User.prototype.setEmailNotificationSubscriptionType User.prototype.setFirstName User.prototype.setGender User.prototype.setHomeCity User.prototype.setLanguage User.prototype.setLastKnownLocation User.prototype.setLastName User.prototype.setPhoneNumber User.prototype.setPushNotificationSubscriptionType User.prototype.setLineId InAppMessageButton InAppMessageButton.prototype.removeAllSubscriptions InAppMessageButton.prototype.removeSubscription InAppMessageButton.prototype.subscribeToClickedEvent FeatureFlag FeatureFlag.prototype.getStringProperty FeatureFlag.prototype.getNumberProperty FeatureFlag.prototype.getBooleanProperty FeatureFlag.prototype.getImageProperty FeatureFlag.prototype.getJsonProperty FeatureFlag.prototype.getTimestampProperty Banner automaticallyShowInAppMessages destroyFeed hideContentCards showContentCards showFeed showInAppMessage deferInAppMessage toggleContentCards toggleFeed changeUser destroy getDeviceId getDeviceId initialize isPushBlocked isPushPermissionGranted isPushSupported logCardClick logCardDismissal logCardImpressions logContentCardImpressions logContentCardClick logCustomEvent logFeedDisplayed logInAppMessageButtonClick logInAppMessageClick logInAppMessageHtmlClick logInAppMessageImpression logPurchase openSession requestPushPermission removeAllSubscriptions removeSubscription requestContentCardsRefresh requestFeedRefresh refreshFeatureFlags requestImmediateDataFlush enableSDK isDisabled setLogger setSdkAuthenticationSignature addSdkMetadata disableSDK subscribeToContentCardsUpdates subscribeToFeedUpdates subscribeToInAppMessage subscribeToSdkAuthenticationFailures toggleLogging unregisterPush wipeData handleBrazeAction subscribeToFeatureFlagsUpdates getAllFeatureFlags logFeatureFlagImpression requestBannersRefresh insertBanner logBannerClick logBannerImpressions getAllBanners subscribeToBannersUpdates isInitialized".split(" "),i=0;i<s.length;i++){for(var m=s[i],k=a.braze,l=m.split("."),j=0;j<l.length-1;j++)k=k[l[j]];k[l[j]]=(new Function("return function "+m.replace(/\\./g,"_")+"(){window.brazeQueue.push(arguments); return true}"))()}window.braze.getDeferredInAppMessage=function(){return new window.braze.InAppMessage};window.braze.getCachedContentCards=function(){return new window.braze.ContentCards};window.braze.getCachedFeed=function(){return new window.braze.Feed};window.braze.getUser=function(){return new window.braze.User};window.braze.getFeatureFlag=function(){return new window.braze.FeatureFlag};window.braze.getBanner=function(){return new window.braze.Banner};window.braze.newBannerFromJson=function(){return new window.braze.Banner};(y=p.createElement(P)).type='text/javascript';
            y.src='https://js.appboycdn.com/web-sdk/5.8/braze.min.js';
            y.async=1;(b=p.getElementsByTagName(P)[0]).parentNode.insertBefore(y,b)
        }(window,document,'script');`,
        }}
      />

      {/* <!-- Google Tag Manager (noscript) --> */}
      <noscript>
        <iframe
          src={`https://www.googletagmanager.com/ns.html?id=${GTM_ID}`}
          height="0"
          width="0"
          style={{ display: 'none', visibility: 'hidden' }}
        />
      </noscript>
      {/* <!-- End Google Tag Manager (noscript) --> */}
      {/* <script id="__SUB_SLICK_JQUERY__"></script> */}

      {/* <!-- flipclock--> */}
      <script id="flipclock-js" src="/flipclock/flipclock.min.js" async defer />
      {/* <!-- --> */}

      {/* accessiBe 404s on non-prod hosts (no registered config) */}
      {keys.runtimeEnv === 'production' && (
        <script
          src="https://acsbapp.com/apps/app/dist/js/app.js"
          async
          defer
          onLoad={onAcsbLoad}
        />
      )}
    </>
  )
}

export function HeadScripts({ keys }: { keys: KeysValue }) {
  const onIreLoad = useScriptInit({
    hasLoadedFn: () => !!window.ire,
    initFn: () => {
      window.ire('identify', { customerId: '', customerEmail: '' })
    },
  })
  const onGtmLoad = useScriptInit({
    hasLoadedFn: () => !!window.dataLayer,
    initFn: () => {
      window.dataLayer.push([
        'event',
        'conversion',
        {
          send_to: 'AW-770514071',
          aw_remarketing_only: true,
        },
      ])
    },
  })

  return (
    <>
      <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `
      window.global ||= window; window.requestTime = Date.now(); window.landingPath = window.location.pathname;
      window.UC_UI_SUPPRESS_CMP_DISPLAY = true;
      `.trim(),
        }}
      />

      {/* <!-- gapi --> */}
      <script async src="https://apis.google.com/js/api.js" />
      <script async src="https://accounts.google.com/gsi/client" />
      {/* <!-- End gapi --> */}

      {/* <!-- Impact Radius --> */}
      <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `
          window.ire_o = 'ire'
          window.ire = window.ire || function () {
            (window.ire.a = window.ire.a || []).push(arguments)
          }`,
        }}
      />
      <script
        src="https://utt.impactcdn.com/A2951962-c164-46b5-86aa-761bb748ae001.js"
        defer
        async
        onLoad={onIreLoad}
      />
      {/* <!-- End Impact Radius --> */}
      <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `
          window.performance.mark('html_load_' + window.location.pathname)
          window.force11UsdPricing = false;`,
        }}
      />
      {/* Redirect from app.mixtiles.com to mixtiles.com */}
      <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `
          if (window.location.host === 'app.mixtiles.com') {
            window.location = 'https://www.mixtiles.com' + window.location.pathname + window.location.search + (window.location.search ? '&' : '?') + 'newSite=true'
          }`,
        }}
      />
      {/* <!--
        Enable traffic origin tracking (for Analytics)
         for further reading: https://www.notion.so/mixtiles/Google-Analytics-changes-9cf7038d73094f4bb51b400e898ad7d1
         --> */}
      <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `
          dataLayer = []
          dataLayer.push({
            originalLocation: document.location.protocol + '//' +
                    document.location.hostname +
                    document.location.pathname +
                    document.location.search
          });`,
        }}
      />
      {/* <!-- End of Analytics  --> */}
      {/* GTM */}
      {keys.isGoogleTagActive.toString() === 'true' && (
        <>
          <script
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: `
            (function (w, d, s, l, i) {
              w[l] = w[l] || [];
              w[l].push({
                'gtm.start':
                        new Date().getTime(), event: 'gtm.js'
              });
            })(window, document, 'script', 'dataLayer', '${GTM_ID}');
          `,
            }}
          />
          <script
            src={`https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`}
            async
            onLoad={onGtmLoad}
          />
        </>
      )}
      <script
        src="https://cdn.jsdelivr.net/npm/search-insights@1.6.3"
        async
        defer
      />
      <script
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
          __html: `
          // Reload once you opt in/out of Facebook Pixel. The ID is as defined in UserCentrics - https://docs.usercentrics.com/#/smart-data-protector
          const fbPixelIdUserCentrics = 'ko1w5PpFl';
          uc.reloadOnOptOut(fbPixelIdUserCentrics);
        `,
        }}
      />
    </>
  )
}
